import { Box, Flex } from 'components'
import { AlertProvider, AuthProvider } from 'components/providers'
import { Variants, motion } from 'motion/react'
import { ReactNode, useContext, useEffect, useState } from 'react'
import { AppStatus, TemplateRouteType } from 'types'
import { forceDark, initTracking, isDevelopment, sleep, useDefaultFont } from 'utils'
import CustomerLogo from './components/CustomerLogo'
import { FileLoaderToast } from './components/FileLoader'
import FooterButtons from './components/FooterButtons'
import {
  DimensionsProvider,
  ExplorerConfigProvider,
  LoadingProvider,
  SelectionCountsProvider,
  SessionContext,
  SessionContextProvider,
  ShortcutProvider,
  TimeoutProvider,
} from './components/providers'
import { useLoadFont } from './utils/desktopFontUtils'
import { SCREEN_TRANSITION } from './utils/globals'
import { isDesktop } from './utils/helpers'
import { useUpdater } from './utils/updater'
import { InitView, ScreensaverView, TraitSelectionView } from './views'
import ResultView from './views/ResultView'

if (isDesktop && !isDevelopment) {
  document.addEventListener('contextmenu', event => event.preventDefault())
  initTracking('Facilities', { facility: 'William Square' }, { defaultTracking: false })
}

export default function App() {
  return (
    <DimensionsProvider height="100vh" width="100vw">
      <Box {...forceDark} display="flex" flex={1}>
        <AlertProvider>
          <AuthProvider>
            <ExplorerConfigProvider cache>
              <LoadingProvider>
                <SelectionCountsProvider>
                  <SessionContextProvider cache={isDevelopment}>
                    <TimeoutProvider>
                      <ShortcutProvider>
                        <Content />
                        <CustomerLogo />
                        <FooterButtons />
                        <FileLoaderToast />
                      </ShortcutProvider>
                    </TimeoutProvider>
                  </SessionContextProvider>
                </SelectionCountsProvider>
              </LoadingProvider>
            </ExplorerConfigProvider>
          </AuthProvider>
        </AlertProvider>
      </Box>
    </DimensionsProvider>
  )
}

function Content() {
  useUpdater()
  useDefaultFont()
  useLoadFont()
  const { status, routeSteps } = useContext(SessionContext)
  const currentStep = routeSteps[routeSteps.length - 1]
  const screen = getScreen(status, currentStep?.type)
  const [nextScreen, setNextScreen] = useState(screen)
  const [prevScreen, setPrevScreen] = useState(screen)

  useEffect(() => {
    ;(async () => {
      const transition = transitions.find(d => d.from.includes(prevScreen) && d.to === screen)
      await sleep(transition?.delay ?? 0)
      setNextScreen(screen)
      await sleep(SCREEN_TRANSITION)
      setPrevScreen(screen)
    })()
  }, [screen])

  return (
    <Flex
      flex={1}
      background="linear-gradient(308deg, #5B9C85 21.33%, #3E7F6D 33.42%, #1A2E73 87.76%)"
    >
      {views.map(({ screen, Component, keepMounted }) => {
        if (keepMounted) {
          if (prevScreen === 'init') return null
        } else {
          if (screen !== nextScreen && screen !== prevScreen) return null
        }

        return (
          <motion.div
            variants={screenAnimations}
            initial="hidden"
            key={screen}
            animate={nextScreen === screen ? 'visible' : 'hidden'}
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: prevScreen === screen ? 1 : nextScreen === screen ? 0 : -1,
              display: 'flex',
            }}
          >
            <Component />
          </motion.div>
        )
      })}
    </Flex>
  )
}

const screenAnimations: Variants = {
  hidden: { opacity: 0, transition: { duration: SCREEN_TRANSITION / 1000, type: 'tween' } },
  visible: { opacity: 1, transition: { duration: 0 } },
}

type Screen = Exclude<AppStatus, 'opt-in' | 'active'> | 'trait-selection' | 'result'

interface View {
  Component: () => ReactNode
  keepMounted: boolean
  screen: Screen
}

function getScreen(status: AppStatus, stepType?: TemplateRouteType): Screen {
  switch (status) {
    case 'opt-in':
      return 'register'
    case 'active':
      if (!stepType) return 'trait-selection'
      return stepType === 'trait' ? 'trait-selection' : 'result'
    default:
      return status
  }
}

const views: View[] = [
  { Component: InitView, keepMounted: false, screen: 'init' },
  { Component: ScreensaverView, keepMounted: false, screen: 'screensaver' },
  { Component: TraitSelectionView, keepMounted: true, screen: 'trait-selection' },
  { Component: ResultView, keepMounted: false, screen: 'result' },
]

interface TransitionDelay {
  delay: number
  from: Screen[]
  to: Screen
}

const transitions: TransitionDelay[] = [
  { delay: 500, from: ['init', 'screensaver'], to: 'register' },
  { delay: 1300, from: ['trait-selection'], to: 'result' },
  { delay: 1000, from: ['register'], to: 'trait-selection' },
]
